import React from 'react'
import styled from 'styled-components'
import { device } from '@/common/constants/device'
import BaseLink from '@/components/common/BaseLink'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { open } from '@/store/modalsSlice'
import {
  FEEDBACK_FORM_MODAL,
  LIMIT_MODAL,
  OFFERS_MODAL,
  OFFERS_PREMIUM_MODAL,
  SEARCH_BAR_MODAL,
} from '@/common/constants/modals'
import { useIsModalVisible } from '@/hooks'
import FeedbackModal from '@/components/FeedbackModal'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import { ANALYTIC_ADS, ANALYTIC_FAVORITES } from '@/common/constants/analytics'
import AdsIconButton from '@/components/Ads/ui/IconButton'
import { useActions } from '@/hooks/useActions'
import { SearchContent } from '@/components/Search/SearchStyled'
import SearchBarHeader from '@/components/Ads/SearchBar/SearchBarHeader'
import OffersPremiumModal from '@/components/Offers/OffersPremiumModal'
import OffersModal from '@/components/Offers/OffersModal'
import CreativesLimitModal from '@/components/Ads/CreativesLimitModal'
import useOffersPremium from '@/components/Creatives/hooks/useOffersPremium'
import Icon from '@/components/common/Icon'
import { setCookie } from 'cookies-next'
import { useTranslations } from 'next-intl'
import { SEARCH_ADS_CLASSIC } from '@/common/constants/search'

export const SEARCH_COOKIE_KEY = 'search_version'

const AdsHeaderContent = styled(SearchContent)`
  padding-top: 16px;
  z-index: 30;
  background-color: #fafafd;
  max-width: 1664px;
  margin: 0 auto;
  position: ${({ isFavorites }) => (isFavorites ? `static` : 'sticky')};
  top: ${({ isFavorites }) => (isFavorites ? `0px` : 'var(--header-height)')};

  .summer-banner {
    margin-bottom: 24px;

    img {
      border-radius: 24px;
    }
  }
`

const SearchHeader = styled.div`
  position: relative;
  z-index: 20;
  background: url('/_next_static/search_draft/ads-mobile-bg.png') top
    center/cover;
  background-position-y: bottom;
  padding: 16px;
  display: flex;
  align-items: center;
  width: 100%;

  @media ${device.tablet} {
    padding: 16px 24px;
    border-radius: 12px;
  }

  @media ${device.laptopM} {
    height: 80px;
    z-index: 30;
    background: url('/_next_static/search_draft/creative-bg.png') top
      center/cover no-repeat;
    border-radius: 24px;
    margin-bottom: ${({ isMarginNone }) => (isMarginNone ? `${0}px` : '16px')};
  }
`

const AdsWrap = styled.div`
  display: flex;
  gap: 16px 50px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;

  @media ${device.laptopM} {
    flex-wrap: nowrap;
  }
`

const Divider = styled.div`
  height: 24px;
  width: 1px;
  margin: 0 16px;
  background: rgba(224, 225, 226, 1);
`

const AdsLink = styled(BaseLink)`
  padding-top: 10px;
  padding-right: 12px;
  padding-bottom: 10px;
  padding-left: 12px;
  gap: 8px;
  border-radius: 6px;
  white-space: nowrap;
  color: #fff;

  &.active {
    background-color: rgba(249, 250, 251, 0.3);
  }
`

const ButtonSwitch = styled(BaseLink)`
  padding-top: 10px;
  padding-right: 16px;
  padding-bottom: 10px;
  padding-left: 12px;
  border-radius: 6px;
  white-space: nowrap;
  color: #fff;
  background-color: rgba(249, 250, 251, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  transition: background 0.26s ease-in-out;

  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`

const ButtonsWrap = styled.div`
  display: flex;
  align-items: center;
`
const SearchWrap = styled.div`
  width: 100%;
`
const AdsButtonWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`

const AdsHeader = ({
  activePage,
  isMarginNone,
  isDisabled,
  isShowSettings = true,
  syncSearch,
  onFilterChange,
  isFavorites = false,
  isOnboarding,
}) => {
  const dispatch = useDispatch()
  const isVisibleFeedbackForm = useIsModalVisible(FEEDBACK_FORM_MODAL)
  const isVisibleLimitModal = useIsModalVisible(LIMIT_MODAL)
  const isVisibleOffersModal = useIsModalVisible(OFFERS_MODAL)
  const isVisibleOffersPremiumModal = useIsModalVisible(OFFERS_PREMIUM_MODAL)
  const { modalContent } = useOffersPremium()

  const t = useTranslations('creatives')
  const tAds = useTranslations('ads')

  const creativesHeaderClasses = classNames({
    disabled: isDisabled,
  })

  const CREATIVES_BUTTONS = {
    All: t('all'),
    Favorites: t('favorites'),
  }

  const isActiveLink = (value) => {
    return classNames({
      active: CREATIVES_BUTTONS[value] === activePage,
      favorites: value === 'Favorites',
    })
  }

  const { setIsVisibleBlackListAdsModal } = useActions()

  const openBlackListModal = () => {
    userAmplitudeAnalytics.trackEvent(`${ANALYTIC_ADS}_black_list_clicked`)
    setIsVisibleBlackListAdsModal(true)
  }

  const openSearchBarModal = () => {
    userAmplitudeAnalytics.trackEvent(`${ANALYTIC_ADS}_search_bar_clicked`)
    dispatch(open(SEARCH_BAR_MODAL))
  }

  const handleSwitchToClassic = () => {
    setCookie(SEARCH_COOKIE_KEY, SEARCH_ADS_CLASSIC)
    userAmplitudeAnalytics.trackEvent(`classic_search_switcher_clicked`)
  }

  return (
    <>
      {isVisibleOffersPremiumModal && !isOnboarding && (
        <OffersPremiumModal modalContent={modalContent} />
      )}
      {isVisibleOffersModal && !isOnboarding && <OffersModal />}
      {isVisibleLimitModal && !isOnboarding && <CreativesLimitModal />}
      {isVisibleFeedbackForm && !isOnboarding && (
        <FeedbackModal survey="search_v2" />
      )}
      <AdsHeaderContent
        className={creativesHeaderClasses}
        isFavorites={isFavorites}
      >
        <SearchHeader className="search-header" isMarginNone={isMarginNone}>
          <AdsWrap>
            <ButtonsWrap>
              <AdsLink
                isChangePath={false}
                className={isActiveLink('All')}
                href="/ads"
              >
                {t('allCreatives')}
              </AdsLink>
              <Divider />
              <AdsLink
                onClick={() => {
                  userAmplitudeAnalytics.trackEvent(
                    `${ANALYTIC_FAVORITES}_clicked`
                  )
                }}
                isChangePath={false}
                className={`${isActiveLink('Favorites')}`}
                href="/ads/favorites"
              >
                {t('favorites')}
              </AdsLink>
            </ButtonsWrap>
            <SearchWrap id="search">
              <SearchBarHeader
                onClick={openSearchBarModal}
                syncSearch={syncSearch}
                onFilterChange={onFilterChange}
              />
            </SearchWrap>
            <AdsButtonWrap>
              {/*<AdsIconButton icon="clock-rewind" />*/}
              {/*<div id="history">*/}
              {/*  <AdsIconButton icon="scales" />*/}
              {/*</div>*/}
              <ButtonSwitch
                href={`/ads?layout=${SEARCH_ADS_CLASSIC}`}
                isChangePath={false}
                onClick={handleSwitchToClassic}
              >
                <Icon icon={'arrow-left'} size="16" color="white" />
                {tAds('newClassicSearch')}
              </ButtonSwitch>
              {isShowSettings && (
                <div id="settings">
                  <button onClick={openBlackListModal}>
                    <AdsIconButton icon="settings" />
                  </button>
                </div>
              )}
            </AdsButtonWrap>
          </AdsWrap>
        </SearchHeader>
      </AdsHeaderContent>
    </>
  )
}

export default AdsHeader
